import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import api from '../api';

const EMPTY_FORM = { name: '', description: '', price: '', stock: '', images: [], video_url: '', video_file: null, cost_price: '', supplier_name: '', supplier_url: '', supplier_product_id: '' };

const AdminProducts = () => {
  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [previews, setPreviews]   = useState([]);
  const [saving, setSaving]       = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [alert, setAlert]         = useState(null);
  const dragIndexRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get('/api/products?admin=true');
    setProducts(data.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setPreviews([]);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || '', price: p.price, stock: p.stock || '', images: [], video_url: p.video_url || '', video_file: null, cost_price: p.cost_price || '', supplier_name: p.supplier_name || '', supplier_url: p.supplier_url || '', supplier_product_id: p.supplier_product_id || '' });
    const existingPreviews = p.images && p.images.length > 0
      ? p.images.map((img) => ({
          id: `existing-${img.id}`,
          existingId: img.id, // vrai id en base (product_images.id) — nécessaire pour dire au backend de garder cette image
          url: img.image_url.startsWith('http') ? img.image_url : `http://localhost:5001${img.image_url}`,
          existing: true,
          file: null,
        }))
      : p.image_url ? [{
          id: 'existing-main',
          existingId: null, // ancien produit sans ligne product_images
          url: p.image_url.startsWith('http') ? p.image_url : `http://localhost:5001${p.image_url}`,
          existing: true,
          file: null,
        }] : [];
    setPreviews(existingPreviews);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newItems = files.map(file => ({
      id: `new-${file.name}-${file.lastModified}-${Math.random()}`,
      url: URL.createObjectURL(file),
      file,
      existing: false,
    }));
    setPreviews(prev => [...prev, ...newItems].slice(0, 6));
    e.target.value = ''; // permet de re-sélectionner le même fichier plus tard
  };

  const removeImage = (id) => {
    setPreviews(prev => prev.filter(p => p.id !== id));
  };

  const handleDragStart = (index) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // nécessaire pour autoriser le drop
  };

  const handleDrop = (index) => {
    const from = dragIndexRef.current;
    if (from === null || from === index) return;
    setPreviews(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(index, 0, moved);
      return updated;
    });
    dragIndexRef.current = null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('description', form.description);
      fd.append('price', form.price);
      fd.append('stock', form.stock || 0);
      fd.append('video_url', form.video_url || '');
      fd.append('cost_price', form.cost_price || 0);
      fd.append('supplier_name', form.supplier_name || '');
      fd.append('supplier_url', form.supplier_url || '');
      fd.append('supplier_product_id', form.supplier_product_id || '');

      // Décrit l'ordre final voulu : images existantes à garder (par id réel)
      // + nouvelles images (par position) — permet au backend de ne PAS tout
      // supprimer quand on ajoute juste une nouvelle photo.
      const imagesOrder = previews.map(p => {
        if (p.existing) {
          return p.existingId ? { type: 'existing', id: p.existingId } : { type: 'legacy', url: p.url };
        }
        return { type: 'new' };
      });
      fd.append('images_order', JSON.stringify(imagesOrder));

      previews.filter(p => p.file).forEach(p => fd.append('images', p.file));

      let savedProduct;
      if (editing) {
        const { data } = await api.put(`/api/admin/products/${editing.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        savedProduct = data.data;
        setAlert({ type: 'success', msg: 'Product updated successfully.' });
      } else {
        const { data } = await api.post('/api/admin/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        savedProduct = data.data;
        setAlert({ type: 'success', msg: 'Product created successfully.' });
      }

      // Upload vidéo MP4 si sélectionnée
      if (form.video_file && savedProduct) {
        setUploadingVideo(true);
        const vfd = new FormData();
        vfd.append('video', form.video_file);
        await api.post(`/api/admin/products/${savedProduct.id}/video`, vfd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setUploadingVideo(false);
      }

      setShowModal(false);
      fetchProducts();
    } catch (err) {
      setUploadingVideo(false);
      setAlert({ type: 'error', msg: err.response?.data?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/api/admin/products/${id}`);
      fetchProducts();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed.');
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/api/admin/products/${id}/toggle`);
      fetchProducts();
    } catch (err) {
      alert('Erreur lors du changement de visibilité.');
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-main">
        <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Products</h1>
            <p>Manage your product catalog.</p>
          </div>
          <button className="btn btn-primary" onClick={openCreate}>+ Add Product</button>
        </div>

        {alert && (
          <div className={`alert alert-${alert.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: '1rem' }}>
            {alert.msg}
            <button onClick={() => setAlert(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        )}

        <div className="card">
          {loading ? (
            <div className="loader"><div className="spinner" /></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Images</th><th>Name</th><th>Prix vente</th><th>Coût</th><th>Marge</th><th>Stock</th><th>Statut</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                      No products yet. Click "+ Add Product" to get started.
                    </td></tr>
                  ) : products.map(p => (
                    <tr key={p.id} style={{ opacity: p.is_visible ? 1 : 0.5 }}>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {p.images && p.images.length > 0 ? (
                            p.images.slice(0, 3).map((img, i) => (
                              <img key={i}
                                src={img.image_url.startsWith('http') ? img.image_url : `http://localhost:5001${img.image_url}`}
                                alt={p.name}
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                            ))
                          ) : p.image_url ? (
                            <img
                              src={p.image_url.startsWith('http') ? p.image_url : `http://localhost:5001${p.image_url}`}
                              alt={p.name}
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                          ) : (
                            <div style={{ width: 40, height: 40, background: '#f0ede8', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                          )}
                          {p.images && p.images.length > 3 && (
                            <div style={{ width: 40, height: 40, background: '#f0ede8', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                              +{p.images.length - 3}
                            </div>
                          )}
                          {p.video_url && (
                            <div style={{ width: 40, height: 40, background: '#e0f2fe', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                              🎥
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <strong>{p.name}</strong>
                        {p.supplier_name && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>🏭 {p.supplier_name}</div>}
                      </td>
                      <td>{parseFloat(p.price).toFixed(2)} MAD</td>
                      <td style={{ color: '#888' }}>{p.cost_price ? `${parseFloat(p.cost_price).toFixed(2)} MAD` : '—'}</td>
                      <td>
                        {p.cost_price > 0 ? (() => {
                          const margin = ((p.price - p.cost_price) / p.price * 100).toFixed(0);
                          return (
                            <span style={{ fontWeight: 700, color: margin >= 30 ? '#10b981' : margin >= 10 ? '#f59e0b' : '#ef4444' }}>
                              {margin}%
                            </span>
                          );
                        })() : '—'}
                      </td>
                      <td>{p.stock ?? '—'}</td>
                      <td>
                        <span style={{
                          padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: p.is_visible ? '#d1fae5' : '#fee2e2',
                          color: p.is_visible ? '#065f46' : '#991b1b'
                        }}>
                          {p.is_visible ? '✅ Visible' : '🙈 Masqué'}
                        </span>
                      </td>
                      <td>{new Date(p.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '.5rem' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)}>Edit</button>
                          <button
                            className="btn btn-sm"
                            onClick={() => handleToggle(p.id)}
                            style={{ background: p.is_visible ? '#f59e0b' : '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
                            {p.is_visible ? '🙈 Masquer' : '👁 Afficher'}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editing ? 'Edit Product' : 'New Product'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSave}>
                  <div className="form-group">
                    <label>Product Name *</label>
                    <input className="form-control" value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea className="form-control" value={form.description} rows={3}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Price ($) *</label>
                      <input type="number" step="0.01" min="0" className="form-control" value={form.price}
                        onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label>Stock</label>
                      <input type="number" min="0" className="form-control" value={form.stock}
                        onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
                    </div>
                  </div>

                  {/* Dropshipping — Infos fournisseur */}
                  <div style={{ background: '#f9f6f1', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: '#555' }}>🏭 Dropshipping / Fournisseur (AliExpress)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: 12 }}>Prix fournisseur (coût) MAD</label>
                        <input type="number" step="0.01" min="0" className="form-control" value={form.cost_price}
                          onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
                          placeholder="Ex: 50.00" />
                        {form.cost_price && form.price && (
                          <div style={{ fontSize: 11, marginTop: 4, color: '#10b981' }}>
                            Marge : {((form.price - form.cost_price) / form.price * 100).toFixed(0)}% — Profit : {(form.price - form.cost_price).toFixed(2)} MAD
                          </div>
                        )}
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: 12 }}>Nom du fournisseur</label>
                        <input className="form-control" value={form.supplier_name}
                          onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))}
                          placeholder="Ex: AliExpress Store XYZ" />
                      </div>
                    </div>
                    <div className="form-group" style={{ margin: '1rem 0 0' }}>
                      <label style={{ fontSize: 12 }}>Lien produit AliExpress</label>
                      <input className="form-control" value={form.supplier_url}
                        onChange={e => setForm(f => ({ ...f, supplier_url: e.target.value }))}
                        placeholder="https://aliexpress.com/item/..." />
                    </div>
                    <div className="form-group" style={{ margin: '1rem 0 0' }}>
                      <label style={{ fontSize: 12 }}>ID produit fournisseur (optionnel)</label>
                      <input className="form-control" value={form.supplier_product_id}
                        onChange={e => setForm(f => ({ ...f, supplier_product_id: e.target.value }))}
                        placeholder="Ex: 1005006789123456" />
                    </div>
                  </div>

                  {/* Vidéo — URL ou fichier MP4 */}
                  <div className="form-group">
                    <label>🎥 Vidéo du produit</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: 8 }}>
                      <div>
                        <p style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Option 1 — URL (YouTube, TikTok)</p>
                        <input
                          className="form-control"
                          value={form.video_url}
                          onChange={e => setForm(f => ({ ...f, video_url: e.target.value, video_file: null }))}
                          placeholder="https://youtube.com/..." />
                      </div>
                      <div>
                        <p style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Option 2 — Fichier MP4</p>
                        <input
                          type="file"
                          accept="video/mp4,video/mov,video/avi"
                          className="form-control"
                          onChange={e => {
                            const file = e.target.files[0];
                            if (file) setForm(f => ({ ...f, video_file: file, video_url: '' }));
                          }}
                          style={{ padding: '.5rem' }} />
                      </div>
                    </div>
                    {(form.video_url || form.video_file) && (
                      <p style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>
                        ✅ {form.video_file ? `Fichier MP4 sélectionné: ${form.video_file.name}` : 'URL vidéo ajoutée'}
                      </p>
                    )}
                    {uploadingVideo && (
                      <p style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>⏳ Upload vidéo en cours...</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Photos du produit (max 6)</label>
                    <input type="file" accept="image/*" multiple className="form-control"
                      onChange={handleFileChange} style={{ padding: '.5rem' }} />
                    {previews.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
                        {previews.map((p, i) => (
                          <div
                            key={p.id}
                            draggable
                            onDragStart={() => handleDragStart(i)}
                            onDragOver={handleDragOver}
                            onDrop={() => handleDrop(i)}
                            style={{
                              position: 'relative',
                              cursor: 'grab',
                              border: '1px solid #eee',
                              borderRadius: 6,
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={p.url}
                              alt={`preview ${i}`}
                              style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(p.id)}
                              style={{
                                position: 'absolute', top: 4, right: 4,
                                width: 22, height: 22, borderRadius: '50%',
                                background: 'rgba(0,0,0,0.65)', color: '#fff',
                                border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 13, lineHeight: 1, padding: 0,
                              }}
                            >
                              ✕
                            </button>
                            {i === 0 && (
                              <span style={{
                                position: 'absolute', bottom: 4, left: 4,
                                background: 'rgba(0,0,0,0.65)', color: '#fff',
                                fontSize: 10, padding: '2px 6px', borderRadius: 4,
                              }}>
                                Principale
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={saving || uploadingVideo}>
                      {uploadingVideo ? 'Upload vidéo...' : saving ? 'Saving…' : editing ? 'Update Product' : 'Create Product'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminProducts;